import { properties } from "@repoflow/common/schema-fields";
import { html, style, script } from "@common/schema/content/fields";

const { createdAt, updatedAt } = properties;

export { createdAt, updatedAt };

export const orgId = {
  type: "string",
};

// Chosen by the caller, not minted — it is what every URL and every element
// reference reads as ("www", "docs"), and a uuid there would make both opaque.
// That is why the model opts into `createIfNotExists`: with a caller-chosen key
// a plain put silently replaces somebody else's site.
export const siteId = {
  label: "Name",
  $ref: "/Name/Required",
  minLength: 1,
  default: "",
};

export const title = {
  label: "Title",
  type: "string",
  minLength: 1,
};

export const status = {
  type: "string",
};

// The document shell every page of this site is composed into — title, meta,
// favicon, the body template and the style and script injected around it. See
// @common/schema/page-config, which declares the shape; this is the one place
// it hangs off.
//
// Two forms of the same field. `config` is what a form renders and what the
// API validates; `storedConfig` is what the table can validate, and the
// difference is the `resolver` union inside `types`. Same split as
// `types`/`storedTypes` on a section, same reason.
export const config = {
  label: "Configuration",
  $ref: "/PageConfig",
};

export const storedConfig = {
  $ref: "/Data/PageConfig",
};

// WHAT LANGUAGES THIS SITE SPEAKS
//
// Declared on the site row rather than inside `config`, and that is deliberate
// twice over. `/PageConfig` is the document SHELL, and every property of it is
// assigned a rule in MERGE_RULES (@common/render/page) that the suite asserts
// covers the shape exactly — so a field there has to answer "what does it mean
// for a PAGE to override this?". For the set of languages a site speaks the
// answer is "nothing": a page is written in one of them, it does not redefine
// them. So `locales` sits beside `title`, and a page's own `locale` sits beside
// its `route`.
//
// A locale is a prefix and a code because those are two different jobs. The
// PREFIX is the first segment of the route an author types — `/es/precios` —
// and it is what makes the language part of the cache key, which is the whole
// reason this feature is shaped the way it is: `site/*` is cached on the path
// and X-Forwarded-Host and nothing else, so a discriminator that is not in the
// path is one that serves the first visitor's language to everybody. The CODE
// is what goes in `<html lang>` and in an hreflang attribute, and it is not
// derivable from the prefix — `/pt` may be `pt-BR`.
export const localeCode = {
  label: "Code",
  $ref: "/Site/Locale/Code",
  default: "",
};

export const localePrefix = {
  label: "Route prefix",
  $ref: "/Site/Locale/Prefix",
  default: "",
};

export const localeLabel = {
  label: "Label",
  type: "string",
  default: "",
};

// `index: "code"` for the same reason `page.elements` carries
// `index: "elementid"`: the list widget only offers a drag handle for an array
// whose rows have an identity of their own, and the order of this list is the
// order the language switcher and the site's own screens read it in.
export const locales = {
  label: "Locales",
  type: "array",
  items: { $ref: "/Site/Locale" },
  index: "code",
  default: [],
};

// Which of them a page with no `locale` of its own is written in, and which one
// `hreflang="x-default"` points at.
//
// An explicit field rather than "the first entry wins", because the first entry
// is a thing a drag inside the list widget changes silently. It has to name a
// code that `locales` declares, and that is the one rule the schema cannot
// state — `assertValidResolved` validates a document, not a relationship
// between two of its properties — so `model/site` checks it by hand.
export const defaultLocale = {
  label: "Default locale",
  $ref: "/Site/Locale/Code",
  default: "",
};

// THE SITE'S TRANSLATION CATALOG.
//
// One flat list of `{ locale, name, value }`, and it is what makes a page's
// markup shared rather than merely similar. A section written against
// `{{site.values.heroTitle}}` renders Spanish on a Spanish page and English on
// an English one WITHOUT either page overriding anything — the catalog is
// consulted at compose time, keyed by the locale the page resolved to.
//
// WHY A CATALOG RATHER THAN A PER-PAGE OVERRIDE, which already worked. The
// override is O(pages x strings) of hand-editing: every translated page has to
// restate every string it changes, and the two pages' element lists are then
// free to drift apart. A catalog is O(strings) and lives in one place — it is
// the model gettext, i18next and every headless CMS with localised fields
// arrived at, for the same reason.
//
// A FLAT LIST rather than a map keyed by locale, so the ordinary `list` widget
// renders it with no new widget and no nesting; `attributes` on /PageConfig is
// the same shape and the same argument. It is LOOKED UP, never walked, so the
// order carries no meaning.
//
// No `index`, for exactly the reason `attributes` gives: it would turn on the
// list widget's drag-reorder keyed by a named property, and two rows added
// before either is named would both key on the empty string.
export const translationLocale = {
  label: "Locale",
  $ref: "/Site/Locale/Code",
  default: "",
};

export const translationName = {
  label: "Name",
  type: "string",
  default: "",
};

export const translationValue = {
  label: "Value",
  type: "string",
  default: "",
};

export const translations = {
  label: "Translations",
  type: "array",
  items: { $ref: "/Site/Translation" },
  default: [],
};

// WHERE A URL NOBODY PUBLISHES AT SENDS THE VISITOR.
//
// One flat list of `{ from, to, prefix, permanent }`, consulted by the
// renderer only after the page match has failed — so a live page is never
// shadowed by a stale row, and a route pack, which is served at the edge
// before the renderer runs, wins over both. See @common/schema/site/redirect
// for the matching rule and api/code/src/render/resolve for where it runs.
//
// ON THE SITE ROW, beside `locales`, for the reason `locales` gives twice
// over: `/PageConfig` is the document SHELL, every property of it has a merge
// rule a suite asserts is complete, and "what does it mean for a PAGE to
// override this" has no answer for a redirect. And it costs nothing to serve
// from here — the renderer reads the site row on every cache miss already, so
// the list arrives with it. gen-2 kept these in a table of their own with an
// index on `from`, which was a second Query per miss; gen-1 kept them exactly
// here (archive/recovered/pagews-admin-portal/src/root/admin/site/config/redirects).
//
// No `index`, for the reason `translations` gives: it would turn on the list
// widget's drag-reorder, and the order of this list carries no meaning — an
// exact row beats a prefix row and a longer prefix beats a shorter, whatever
// order they were typed in. `maxItems` is the only ceiling: gen-2's per-plan
// quota was 50, this service has no plans to hang one off, and a bound on the
// row is the honest form of the same limit.
export const redirectFrom = {
  label: "From path",
  $ref: "/Site/Redirect/From",
  default: "/",
};

export const redirectTo = {
  label: "To",
  $ref: "/Site/Redirect/To",
  default: "/",
};

export const redirectPrefix = {
  label: "Everything below it too",
  type: "boolean",
  default: false,
};

export const redirectPermanent = {
  label: "Permanent (301)",
  type: "boolean",
  default: false,
};

export const redirects = {
  label: "Redirects",
  type: "array",
  items: { $ref: "/Site/Redirect" },
  maxItems: 200,
  default: [],
};

// Extra documents these fields reference. Spread by public.js and data.js the
// same way UtilsSchema is, and the same way @common/schema/page/fields exports
// /Page/Route.
// THE BLOG'S SITE-LEVEL HALF: the authors a post can name, and the three
// templates its elements render through.
//
// Beside `locales` and `translations` for the same reason they are beside
// `title` and not inside `config`: `/PageConfig` is the document shell and every
// property of it needs an answer to "what does a page overriding this mean?",
// and a page does not override who writes for the site or what its blog looks
// like. See ./blog for what gen-2 kept here instead, and for why the templates
// are here and not among the site's sections.
//
// Two forms, like `config`/`storedConfig`. `blog` is what a form renders and
// the API validates; `storedBlog` admits null because every site row written
// before this field serializes it as null (datorm writes an absent property
// that way), and a stored shape that refused null would refuse the whole row
// on its next write — which is P209, and this field is not going to widen it.
// The resolver and the model coalesce, so nothing above the row sees the null.
export const authorId = {
  label: "Id",
  $ref: "/Name/Required",
  minLength: 1,
  default: "",
};

export const authorName = {
  label: "Name",
  type: "string",
  maxLength: 256,
  default: "",
};

export const authorImage = {
  label: "Image",
  $ref: "/Types/Image",
  default: { url: "", description: "" },
};

export const authorBio = {
  label: "Bio",
  type: "string",
  textarea: true,
  default: "",
};

export const authors = {
  label: "Authors",
  type: "array",
  items: { $ref: "/Site/Blog/Author" },
  index: "authorId",
  maxItems: 200,
  default: [],
};

// ONE TEMPLATE: the same three fields a section is made of, and the same
// widgets, so the template editor is the section editor's Code tab. No
// `types`/`values` — what a template sees is decided by the element type it
// renders (see @common/render/blog), not declared on it — and no `frame`: the
// element carries one, as every element does.
//
// Per KIND rather than a list, because there are exactly three and the kind is
// what an element renders through. Each admits null on the way in: null is how
// the editor says "back to the built-in", and the model drops the key.
export const templateHtml = { ...html };
export const templateStyle = { ...style };
export const templateScript = { ...script };

const templateOfKind = (label) => ({
  label,
  type: ["object", "null"],
  properties: {
    html: templateHtml,
    style: templateStyle,
    script: templateScript,
  },
});

export const templates = {
  label: "Templates",
  $ref: "/Site/Blog/Templates",
  default: {},
};

export const blog = {
  label: "Blog",
  $ref: "/Site/Blog",
  default: { authors: [], templates: {} },
};

export const storedBlog = {
  type: ["object", "null"],
};

export default [
  {
    // NOT `/TextLang`'s `^[a-z]{2}-[A-Z]{2}$`, which @repoflow/schema/utils
    // already declares and which is already spread into every schema here. That
    // pattern requires a region and so cannot express `es`, `pt` or `zh-Hant` —
    // the commonest values there are for `<html lang>`. Shadowing it would not
    // work either: both documents land in one array and SchemaLib.get takes the
    // first match by id, which arrives with UtilsSchema at the top.
    //
    // This is BCP-47 narrowed to what a site declares: a 2-3 letter language,
    // an optional 4-letter script, and an optional region that is either two
    // letters or three digits. Empty is allowed so a site that has never opened
    // the screen validates.
    id: "/Site/Locale/Code",
    type: "string",
    maxLength: 32,
    pattern: "^$|^[a-z]{2,3}(-[A-Z][a-z]{3})?(-([A-Z]{2}|[0-9]{3}))?$",
  },
  {
    // ONE segment, and a strict subset of /Page/Route's charset, so that
    // `prefix + route` is always itself a storable route. Empty is the locale
    // served at the root — see model/site for why at most one may be empty.
    id: "/Site/Locale/Prefix",
    type: "string",
    maxLength: 32,
    pattern: "^$|^/[a-z0-9-]+$",
  },
  {
    // The three together are one entry. `name` matches a key in the site's
    // `values`, which is what the catalog overlays — so a name with no
    // corresponding value is inert rather than an error, and a value with no
    // translation for the current locale falls through to the site's own.
    id: "/Site/Translation",
    type: "object",
    properties: {
      locale: translationLocale,
      name: translationName,
      value: translationValue,
    },
    required: ["locale", "name"],
  },
  {
    // The path a visitor asked for. /Page/Route's charset plus `_`, which old
    // URLs carry more often than new routes do; a leading slash is REQUIRED
    // rather than admitted, so a row cannot be blank the way a page's route
    // can. Trailing slashes are stripped on the way in — model/site
    // canonicalises — so `/old` and `/old/` are one row, exactly as they are
    // one request by the time the viewer-request function has 301'd the first
    // into the second.
    id: "/Site/Redirect/From",
    type: "string",
    minLength: 1,
    maxLength: 256,
    pattern: "^/[/.:a-zA-Z0-9_-]*$",
  },
  {
    // Where it goes: a path on this site, or an absolute http(s) URL. The
    // negative lookahead is what refuses `//host/…`, which a browser reads as
    // protocol-relative and would send the visitor off-site under a value
    // that reads as a path; every other scheme (`javascript:`, `data:`) fails
    // the pattern outright.
    id: "/Site/Redirect/To",
    type: "string",
    minLength: 1,
    maxLength: 2048,
    pattern: "^(?:/(?!/)[^\\s]*|https?://[^\\s]+)$",
  },
  {
    // The four together are one row. `prefix` is what makes it answer
    // everything below `from` at a whole segment, with the remainder carried
    // onto `to`; `permanent` chooses 301 over the default 302, and defaults
    // OFF because a browser caches a 301 without expiry and a mistyped one
    // cannot be recalled from anyone who already followed it.
    id: "/Site/Redirect",
    type: "object",
    properties: {
      from: redirectFrom,
      to: redirectTo,
      prefix: redirectPrefix,
      permanent: redirectPermanent,
    },
    required: ["from", "to"],
  },
  {
    id: "/Site/Locale",
    type: "object",
    properties: {
      code: localeCode,
      prefix: localePrefix,
      label: localeLabel,
    },
    required: ["code"],
  },
  {
    id: "/Site/Blog/Author",
    type: "object",
    properties: {
      authorId,
      name: authorName,
      image: authorImage,
      bio: authorBio,
    },
    required: ["authorId", "name"],
  },
  {
    // The form target of the template editor: one template, whole. What the
    // three kinds share, declared once so the editor's field list and the
    // widgets behind it are one thing.
    id: "/Site/Blog/Template",
    type: "object",
    properties: {
      html: templateHtml,
      style: templateStyle,
      script: templateScript,
    },
  },
  {
    // What a site has written, by kind. A kind that is absent is the built-in.
    id: "/Site/Blog/Templates",
    type: "object",
    properties: {
      post: templateOfKind("Post"),
      list: templateOfKind("List"),
      collection: templateOfKind("Collection"),
    },
  },
  {
    id: "/Site/Blog",
    type: "object",
    properties: {
      authors,
      templates,
    },
  },
];
